// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 49e654c3-b4ab-49f1-8d7b-02477c0152dc

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// My dashboard — a user-composable grid of modules, the owner's ask verbatim:
// "some type of update screen that shows warnings on the dashboard per
// client... a dynamic build dashboard... you pick the modules."
//
// Design rules this file inherits from the rest of the codebase:
//   - NO new endpoints. Every number comes from a data path another screen
//     already exercises: store.listFactSheets (local cache), /contracts/
//     renewals (ContractsRenewals.js), /surveys (SurveyWorkflow.js),
//     /history (HistoryTab.js), /master/overview (MasterDashboard.js),
//     /finops/overview (FinOps.js — its api() shape is copied, the component
//     is not imported).
//   - The master workspace NEVER blends clients. Every grouping below keys by
//     tenantId first; per-client warnings render under a per-client header.
//   - The contracts/surveys APIs refuse an unscoped master request by design,
//     so the master warnings scan names each client explicitly and CAPS how
//     many it calls — and says exactly which clients it did not check.
//   - Completion arithmetic is store.completionOver's alone. This file only
//     ever reads the `completion` the store already computed per row, and
//     averages those — it never rescores a field.
//   - Every capped list announces what it dropped; every failed check says it
//     failed instead of rendering an empty box.
//
// Layout persistence: an ordered array of module keys per user+workspace in
// localStorage under `eams.dashboard.v1.<email>.<tenantId>`. The read is
// guarded — every localStorage parse in this codebase is.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Grid, Button, IconButton, Menu, MenuItem,
  Chip, Alert, CircularProgress, LinearProgress, Tooltip, Link, Divider,
  ListItemIcon, ListItemText,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpBubble from './HelpBubble';
import store from './store';
import { getConfig } from './config';
import { getIdToken } from './auth';
import { typeMeta } from './schema';
import {
  specsOf, isServerRecord, buildLinkage, analyzeObsolescence, CLASSES,
} from './Obsolescence';

const HEAD = 'sectionHeader.main';
const GREY = '#5C6B7A';
const MUTED = '#9AA6B2';

// Warnings thresholds — named so the on-screen labels and the checks cannot
// drift apart.
const RENEWAL_HORIZON_DAYS = 60;
const COMPLETION_THRESHOLD = 50;
// How many clients the master warnings scan will call the contracts/surveys
// APIs for. Those routes require a named tenant per request (by design — see
// backend/lambda/index.js), so an uncapped scan is one round trip per client
// per source. The cap is announced on screen with the exact clients skipped.
const MASTER_SCAN_CAP = 6;
// Row caps inside modules — each one announces what it dropped.
const RENEWALS_SHOWN = 5;
const COMPLETION_ROWS_SHOWN = 8;
const ACTIVITY_SHOWN = 8;
const MASTER_COMPLETION_ROWS = 10;

// ---------------------------------------------------------------------------
// Module registry. `size` is the md Grid width; warnings is the centerpiece
// and spans the row.
// ---------------------------------------------------------------------------
const MODULE_DEFS = {
  warnings: { label: 'Warnings & attention items', size: 12 },
  renewals: { label: 'Upcoming renewals', size: 6 },
  completion: { label: 'Completion', size: 6 },
  activity: { label: 'Recent activity', size: 6 },
  spend: { label: 'Cloud spend', size: 6 },
  obsolescence: { label: 'Obsolescence risk', size: 6 },
};
const DEFAULT_LAYOUT = ['warnings', 'renewals', 'completion', 'activity'];

const layoutKeyFor = (email, tenantId) =>
  `eams.dashboard.v1.${(email || 'local').toLowerCase()}.${tenantId}`;

// Guarded read — same idiom as SurveyWorkflow's readLocal. A stored value
// that is not an array of known module keys is treated as absent, never
// thrown at the user.
function readLayout(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return DEFAULT_LAYOUT;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_LAYOUT;
    const seen = new Set();
    const clean = parsed.filter((k) => {
      if (!MODULE_DEFS[k] || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    // An explicitly emptied dashboard is a valid saved state — only an
    // unreadable one falls back to the default.
    return clean;
  } catch (e) {
    return DEFAULT_LAYOUT;
  }
}
function writeLayout(key, layout) {
  try { localStorage.setItem(key, JSON.stringify(layout)); } catch (e) { /* quota */ }
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

// The same guarded fetch shape ContractsRenewals/FinOps/SurveyWorkflow use —
// an error envelope is often HTML, not JSON, and must surface as the real
// status rather than "Unexpected token <".
function useApi() {
  return useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    let body = {};
    let parseFailed = false;
    try { body = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) {
      const err = new Error(body.error || body.message || `request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return body;
  }, []);
}

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

const fmtMoney = (v, ccy) => {
  if (v === undefined || v === null || v === '' || !Number.isFinite(Number(v))) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy || 'USD' }).format(Number(v)); }
  catch (e) { return `${v} ${ccy || ''}`; }
};

// The smaller of daysToEnd/daysToRenewal — the date a renewal conversation
// actually cares about. Same rule as ContractsRenewals.soonestDays.
function dueDays(r) {
  const vals = [r.daysToEnd, r.daysToRenewal].filter((d) => d !== undefined && d !== null);
  return vals.length ? Math.min(...vals) : null;
}

const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return d.toLocaleDateString();
};

// Local checks over one workspace's cached rows — shared by the client
// warnings module and the per-client groups in master. `rows` must already be
// a SINGLE tenant's rows; the callers own that scoping.
function localWarningsFor(rows) {
  const items = [];
  const servers = rows.filter((fs) => fs.type === 'ITComponent' && isServerRecord(specsOf(fs)));
  if (servers.length) {
    const deployments = rows.filter((fs) => fs.type === 'Deployment');
    const applications = rows.filter((fs) => fs.type === 'Application');
    const analysis = analyzeObsolescence(servers, buildLinkage(servers, deployments, applications), new Date());
    const eos = analysis.counts[CLASSES.EOS] || 0;
    const unknown = analysis.counts[CLASSES.UNKNOWN] || 0;
    if (eos) {
      items.push({
        key: 'eos', severity: 'error', count: eos, view: 'obsolescence',
        text: `server${eos === 1 ? '' : 's'} past end of support (recorded or inferred — Obsolescence shows which)`,
      });
    }
    if (unknown) {
      items.push({
        key: 'eosUnknown', severity: 'warning', count: unknown, view: 'obsolescence',
        text: `server${unknown === 1 ? '' : 's'} with no usable support date — missing data, not "supported"`,
      });
    }
  }
  const low = rows.filter((r) => (r.completion || 0) < COMPLETION_THRESHOLD).length;
  if (low) {
    items.push({
      key: 'completion', severity: 'warning', count: low, view: 'inventory',
      text: `of ${rows.length} fact sheets below ${COMPLETION_THRESHOLD}% complete`,
    });
  }
  return items;
}

// Contract + survey checks for ONE named tenant, via the APIs those screens
// already use. Returns { items, unchecked } and never throws.
async function apiWarningsFor(api, tenantId) {
  const items = [];
  const unchecked = [];
  try {
    const list = await api(`/contracts/renewals?tenantId=${encodeURIComponent(tenantId)}&days=${RENEWAL_HORIZON_DAYS}`);
    const rows = Array.isArray(list) ? list : [];
    const past = rows.filter((r) => { const d = dueDays(r); return d !== null && d < 0; }).length;
    const soon = rows.length - past;
    if (past) {
      items.push({
        key: 'renewPast', severity: 'error', count: past, view: 'contracts',
        text: `contract${past === 1 ? '' : 's'} already past an end or renewal date`,
      });
    }
    if (soon) {
      items.push({
        key: 'renewSoon', severity: 'warning', count: soon, view: 'contracts',
        text: `contract${soon === 1 ? '' : 's'} renew${soon === 1 ? 's' : ''} or expire${soon === 1 ? 's' : ''} within ${RENEWAL_HORIZON_DAYS} days`,
      });
    }
  } catch (e) {
    unchecked.push({ source: 'Contracts & renewals', reason: e.message });
  }
  try {
    const data = await api(`/surveys?tenantId=${encodeURIComponent(tenantId)}`);
    const rows = Array.isArray(data) ? data : (data.campaigns || []);
    const stuck = rows.filter((c) => c.status === 'open'
      && (((c.counts || {}).pending || 0) + ((c.counts || {}).inProgress || 0)) > 0).length;
    if (stuck) {
      items.push({
        key: 'surveys', severity: 'warning', count: stuck, view: 'surveys',
        text: `open survey campaign${stuck === 1 ? '' : 's'} with assignees who have not finished`,
      });
    }
  } catch (e) {
    unchecked.push({ source: 'Discovery surveys', reason: e.message });
  }
  return { items, unchecked };
}

// ---------------------------------------------------------------------------
// Presentational bits
// ---------------------------------------------------------------------------

function WarningRow({ item, onClick }) {
  return (
    <Stack
      direction="row" alignItems="center" spacing={1.5}
      onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      sx={{
        px: 1, py: 0.75, borderRadius: 1, cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Chip size="small" color={item.severity} label={item.count} sx={{ fontWeight: 700, minWidth: 44 }} />
      <Typography sx={{ fontSize: 13, flex: 1 }}>{item.text}</Typography>
      <ArrowForwardIcon sx={{ fontSize: 15, color: MUTED }} />
    </Stack>
  );
}

function UncheckedNote({ unchecked }) {
  if (!unchecked.length) return null;
  return (
    <Typography variant="caption" sx={{ color: (t) => t.palette.warning.main, display: 'block', mt: 1 }}>
      Could not check: {unchecked.map((u) => `${u.source} (${u.reason})`).join('; ')}.
    </Typography>
  );
}

function ModuleLoading() {
  return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
}

function EmptyNote({ children }) {
  return <Typography sx={{ fontSize: 13, color: MUTED, py: 1 }}>{children}</Typography>;
}

// The tile chrome every module renders inside: title, optional subtitle and
// the ⋮ menu (move up / move down / remove).
function ModuleFrame({ title, subtitle, onMoveUp, onMoveDown, onRemove, canMoveUp, canMoveDown, children }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: HEAD }}>{title}</Typography>
          {subtitle && <Typography variant="caption" sx={{ color: GREY }}>{subtitle}</Typography>}
        </Box>
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label={`Options for ${title}`}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
          <MenuItem disabled={!canMoveUp} onClick={() => { setAnchor(null); onMoveUp(); }}>
            <ListItemIcon><ArrowUpwardIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Move up</ListItemText>
          </MenuItem>
          <MenuItem disabled={!canMoveDown} onClick={() => { setAnchor(null); onMoveDown(); }}>
            <ListItemIcon><ArrowDownwardIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Move down</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setAnchor(null); onRemove(); }}>
            <ListItemIcon><CloseIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Remove from dashboard</ListItemText>
          </MenuItem>
        </Menu>
      </Stack>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// WARNINGS — the centerpiece.
// ---------------------------------------------------------------------------
function WarningsModule({ tenantId, inMaster, refreshToken, onNavigate, onOpenTenant }) {
  const api = useApi();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let live = true;
    setState({ loading: true });
    (async () => {
      try {
        if (!inMaster) {
          const rows = await store.listFactSheets(tenantId);
          const local = localWarningsFor(rows || []);
          const remote = await apiWarningsFor(api, tenantId);
          if (!live) return;
          setState({ loading: false, items: [...remote.items, ...local], unchecked: remote.unchecked });
          return;
        }
        // MASTER: per client, never blended. Local cache checks run for every
        // client with cached rows; API checks run for the first
        // MASTER_SCAN_CAP clients only (one round trip per client per source
        // — the routes refuse an unscoped master request by design).
        const [tenants, allRows] = await Promise.all([
          store.listTenants(),
          store.listFactSheets('optimum'),
        ]);
        const clients = (tenants || [])
          .filter((t) => t.kind !== 'master' && t.status !== 'archived')
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        // listFactSheets('optimum') returns EVERY tenant's cached rows — key
        // the grouping by tenantId, nothing else.
        const rowsByTenant = new Map();
        (allRows || []).forEach((r) => {
          if (!r.tenantId) return;
          if (!rowsByTenant.has(r.tenantId)) rowsByTenant.set(r.tenantId, []);
          rowsByTenant.get(r.tenantId).push(r);
        });
        const scanned = clients.slice(0, MASTER_SCAN_CAP);
        const skipped = clients.slice(MASTER_SCAN_CAP);
        const remoteByTenant = new Map();
        await Promise.all(scanned.map(async (t) => {
          remoteByTenant.set(t.id, await apiWarningsFor(api, t.id));
        }));
        const groups = clients.map((t) => {
          const remote = remoteByTenant.get(t.id) || { items: [], unchecked: [] };
          const local = rowsByTenant.has(t.id) ? localWarningsFor(rowsByTenant.get(t.id)) : [];
          return {
            id: t.id,
            name: t.name,
            items: [...remote.items, ...local],
            unchecked: remote.unchecked,
            cached: rowsByTenant.has(t.id),
            apiScanned: remoteByTenant.has(t.id),
          };
        });
        if (!live) return;
        setState({
          loading: false,
          groups,
          cachedCount: groups.filter((g) => g.cached).length,
          clientCount: clients.length,
          skippedNames: skipped.map((t) => t.name),
        });
      } catch (e) {
        if (live) setState({ loading: false, error: e.message || 'the warnings scan failed' });
      }
    })();
    return () => { live = false; };
  }, [api, tenantId, inMaster, refreshToken]);

  if (state.loading) return <ModuleLoading />;
  if (state.error) return <Alert severity="error">{state.error}</Alert>;

  if (!inMaster) {
    const items = state.items || [];
    return (
      <Box>
        {items.length === 0 && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
            <CheckCircleOutlineIcon sx={{ color: (t) => t.palette.success.main, fontSize: 20 }} />
            <Typography sx={{ fontSize: 13, color: GREY }}>
              Nothing needs attention from the checks that ran (renewals within {RENEWAL_HORIZON_DAYS} days,
              support dates, completion below {COMPLETION_THRESHOLD}%, open surveys).
            </Typography>
          </Stack>
        )}
        <Stack spacing={0.25}>
          {items.map((it) => (
            <WarningRow key={it.key} item={it} onClick={() => onNavigate(it.view)} />
          ))}
        </Stack>
        <UncheckedNote unchecked={state.unchecked || []} />
      </Box>
    );
  }

  const groups = (state.groups || []).filter((g) => g.items.length || g.unchecked.length);
  const quiet = (state.clientCount || 0) - groups.length;
  return (
    <Box>
      {groups.length === 0 && (
        <EmptyNote>
          No warnings from the checks that ran across {state.clientCount} client{state.clientCount === 1 ? '' : 's'}.
        </EmptyNote>
      )}
      <Grid container spacing={2}>
        {groups.map((g) => (
          <Grid item xs={12} md={6} key={g.id}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: HEAD, flex: 1 }}>{g.name}</Typography>
                <Chip size="small" label={`${g.items.length} warning${g.items.length === 1 ? '' : 's'}`}
                  sx={{ height: 18, fontSize: 10, bgcolor: 'chipMuted.main', color: HEAD }} />
              </Stack>
              <Stack spacing={0.25}>
                {g.items.map((it) => (
                  <WarningRow key={it.key} item={it}
                    onClick={() => { onOpenTenant(g.id); onNavigate(it.view); }} />
                ))}
              </Stack>
              <UncheckedNote unchecked={g.unchecked} />
            </Box>
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1.5 }}>
        Contract and survey checks called the API for the first {Math.min(MASTER_SCAN_CAP, state.clientCount)} of {state.clientCount} clients
        {state.skippedNames && state.skippedNames.length
          ? ` — not yet checked: ${state.skippedNames.join(', ')}. Enter those workspaces to check them.`
          : '.'}
        {' '}Support-date and completion checks cover the {state.cachedCount} client workspace{state.cachedCount === 1 ? '' : 's'} cached
        in this browser — a client that has never been opened here is not scanned, not "clean".
        {quiet > 0 && ` ${quiet} client${quiet === 1 ? ' shows' : 's show'} no warnings from those checks.`}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// RENEWALS — next few renewals with days-left chips.
// ---------------------------------------------------------------------------
function RenewalsModule({ tenantId, inMaster, refreshToken, onNavigate }) {
  const api = useApi();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (inMaster) { setState({ loading: false }); return undefined; }
    let live = true;
    setState({ loading: true });
    api(`/contracts/renewals?tenantId=${encodeURIComponent(tenantId)}&days=365`)
      .then((list) => { if (live) setState({ loading: false, rows: Array.isArray(list) ? list : [] }); })
      .catch((e) => { if (live) setState({ loading: false, error: e.message }); });
    return () => { live = false; };
  }, [api, tenantId, inMaster, refreshToken]);

  if (inMaster) {
    return (
      <EmptyNote>
        Contracts live per client, and the renewals API reads one workspace at a time.
        The Warnings module above checks the nearest renewals per client; enter a client
        workspace for its full calendar.
      </EmptyNote>
    );
  }
  if (state.loading) return <ModuleLoading />;
  if (state.error) return <Alert severity="error">{state.error}</Alert>;

  const rows = state.rows || [];
  const shown = rows.slice(0, RENEWALS_SHOWN);
  const dropped = rows.length - shown.length;
  if (!rows.length) return <EmptyNote>Nothing renews or expires inside 365 days.</EmptyNote>;

  return (
    <Box>
      <Stack spacing={0.5}>
        {shown.map((r) => {
          const days = dueDays(r);
          const chip = days === null
            ? { label: 'no date', color: 'default' }
            : days < 0
              ? { label: 'past due', color: 'error' }
              : { label: `${days}d left`, color: days < 30 ? 'error' : days < 90 ? 'warning' : 'default' };
          return (
            <Stack key={r.id} direction="row" alignItems="center" spacing={1}
              onClick={() => onNavigate('contracts')} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') onNavigate('contracts'); }}
              sx={{ px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>{r.name}</Typography>
                <Typography noWrap variant="caption" sx={{ color: GREY }}>
                  {r.vendor ? `${r.vendor} · ` : ''}{fmtDate(r.dueDate)}
                  {r.annualCost !== undefined ? ` · ${fmtMoney(r.annualCost, r.currency)}/yr` : ''}
                </Typography>
              </Box>
              <Chip size="small" color={chip.color} label={chip.label} sx={{ fontWeight: 700 }} />
            </Stack>
          );
        })}
      </Stack>
      {dropped > 0 && (
        <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
          Showing the {shown.length} soonest of {rows.length} inside 365 days —{' '}
          <Link component="button" underline="hover" onClick={() => onNavigate('contracts')} sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}>
            open the renewal calendar
          </Link>{' '}for the rest.
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// COMPLETION — the store's own per-row completion, averaged. Never rescored.
// ---------------------------------------------------------------------------
function CompletionModule({ tenantId, inMaster, refreshToken, onNavigate }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let live = true;
    setState({ loading: true });
    (async () => {
      try {
        const rows = await store.listFactSheets(inMaster ? 'optimum' : tenantId);
        if (!live) return;
        // Group by TYPE in a client workspace, by TENANT in master — a
        // master average across clients would blend them into one number.
        const keyOf = inMaster ? (r) => r.tenantId : (r) => r.type;
        const groups = new Map();
        (rows || []).forEach((r) => {
          const k = keyOf(r);
          if (!k) return;
          if (!groups.has(k)) groups.set(k, { key: k, n: 0, sum: 0 });
          const g = groups.get(k);
          g.n += 1;
          g.sum += (r.completion || 0);
        });
        const list = [...groups.values()]
          .map((g) => ({ ...g, avg: Math.round(g.sum / g.n) }))
          .sort((a, b) => b.n - a.n);
        setState({ loading: false, list, total: (rows || []).length });
      } catch (e) {
        if (live) setState({ loading: false, error: e.message || 'could not read the workspace' });
      }
    })();
    return () => { live = false; };
  }, [tenantId, inMaster, refreshToken]);

  if (state.loading) return <ModuleLoading />;
  if (state.error) return <Alert severity="error">{state.error}</Alert>;
  const cap = inMaster ? MASTER_COMPLETION_ROWS : COMPLETION_ROWS_SHOWN;
  const list = state.list || [];
  const shown = list.slice(0, cap);
  if (!list.length) return <EmptyNote>No fact sheets in this workspace yet.</EmptyNote>;

  return (
    <Box>
      <Stack spacing={1}>
        {shown.map((g) => (
          <Box key={g.key}
            onClick={() => onNavigate('inventory')} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onNavigate('inventory'); }}
            sx={{ cursor: 'pointer', px: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, flex: 1 }} noWrap>
                {inMaster ? g.key : typeMeta(g.key).label}
              </Typography>
              <Typography variant="caption" sx={{ color: GREY }}>{g.n} · {g.avg}%</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={g.avg}
              sx={{ height: 6, borderRadius: 3, bgcolor: 'chipMuted.main' }} />
          </Box>
        ))}
      </Stack>
      {list.length > cap && (
        <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
          Showing the {cap} largest of {list.length} {inMaster ? 'clients' : 'types'} ({state.total} fact sheets in all).
        </Typography>
      )}
      {inMaster && (
        <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
          Averages come from this browser's cached workspaces, keyed per client — never blended.
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ACTIVITY — /master/overview's feed in master; the /history audit trail in a
// client workspace (the same source HistoryTab reads).
// ---------------------------------------------------------------------------
function ActivityModule({ tenantId, inMaster, refreshToken, onOpenTenant }) {
  const api = useApi();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let live = true;
    setState({ loading: true });
    (async () => {
      try {
        if (inMaster) {
          const data = await api('/master/overview');
          if (live) setState({ loading: false, master: (data && data.activity) || [] });
          return;
        }
        const data = await api(`/history?tenantId=${encodeURIComponent(tenantId)}&limit=${ACTIVITY_SHOWN}`);
        const items = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
        if (live) {
          setState({
            loading: false,
            items: items.map((e) => ({
              id: e.id,
              at: e.at,
              actor: e.actor,
              action: e.action,
              entityName: e.entity_name || e.entityName || e.entity_type || e.entityType || '',
            })),
            total: Array.isArray(data) ? items.length : (data.total || items.length),
          });
        }
      } catch (e) {
        if (!live) return;
        if (e.status === 404) {
          setState({ loading: false, unavailable: 'The change-history API is not deployed in this environment yet.' });
        } else {
          setState({ loading: false, error: e.message });
        }
      }
    })();
    return () => { live = false; };
  }, [api, tenantId, inMaster, refreshToken]);

  if (state.loading) return <ModuleLoading />;
  if (state.unavailable) return <EmptyNote>{state.unavailable}</EmptyNote>;
  if (state.error) return <Alert severity="error">{state.error}</Alert>;

  if (inMaster) {
    const acts = state.master || [];
    if (!acts.length) return <EmptyNote>Nothing recorded yet.</EmptyNote>;
    return (
      <Stack spacing={1}>
        {acts.map((a, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={i} sx={{ borderLeft: (t) => `3px solid ${a.action === 'DELETE' ? t.palette.error.main : t.palette.primary.main}`, pl: 1.2 }}>
            <Typography sx={{ fontSize: 13, color: HEAD }}>
              <b>{a.entityName || a.entityType}</b> {String(a.action || '').toLowerCase()}d in{' '}
              <Link component="button" underline="hover" onClick={() => onOpenTenant(a.tenantId)}
                sx={{ fontSize: 13, fontWeight: 700, verticalAlign: 'baseline' }}>
                {a.tenantId}
              </Link>
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>
              {when(a.at)}{a.actor ? ` — ${a.actor}` : ''}
            </Typography>
          </Box>
        ))}
      </Stack>
    );
  }

  const items = state.items || [];
  if (!items.length) return <EmptyNote>No audit entries recorded for this workspace yet.</EmptyNote>;
  return (
    <Box>
      <Stack spacing={1}>
        {items.map((e) => (
          <Box key={e.id} sx={{ borderLeft: (t) => `3px solid ${e.action === 'DELETE' ? t.palette.error.main : t.palette.primary.main}`, pl: 1.2 }}>
            <Typography sx={{ fontSize: 13, color: HEAD }}>
              <b>{e.entityName}</b> {String(e.action || '').toLowerCase() === 'insert' ? 'created' : `${String(e.action || '').toLowerCase()}d`}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>
              {when(e.at)}{e.actor ? ` — ${e.actor}` : ''}
            </Typography>
          </Box>
        ))}
      </Stack>
      {state.total > items.length && (
        <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
          The latest {items.length} of {state.total} recorded changes.
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SPEND — /finops/overview, the same fetch shape FinOps.js uses (copied, per
// that screen's own single-tenant-by-construction contract; the component
// itself is not imported).
// ---------------------------------------------------------------------------
function SpendModule({ tenantId, refreshToken, onNavigate }) {
  const api = useApi();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let live = true;
    setState({ loading: true });
    const qs = tenantId && tenantId !== 'optimum' ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    api(`/finops/overview${qs}`)
      .then((data) => { if (live) setState({ loading: false, data }); })
      .catch((e) => { if (live) setState({ loading: false, error: e.message }); });
    return () => { live = false; };
  }, [api, tenantId, refreshToken]);

  if (state.loading) return <ModuleLoading />;
  if (state.error) return <Alert severity="error">{state.error}</Alert>;

  const data = state.data || {};
  if (!data.connected) {
    return (
      <EmptyNote>
        No cloud connection for this workspace{data.reason ? ` (${data.reason})` : ''} — set one up under{' '}
        <Link component="button" underline="hover" onClick={() => onNavigate('admin')} sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}>
          Administration → Cloud connections
        </Link>.
      </EmptyNote>
    );
  }
  const spend = data.spendSummary;
  if (!spend || !spend.available) {
    return (
      <Alert severity="warning">
        Connected, but the spend summary could not be read this time: {(spend && spend.reason) || 'no reason reported'}.
      </Alert>
    );
  }
  const months = spend.months || [];
  const unit = spend.unit || 'USD';
  // The API's month list includes the current month to date (FinOps labels it
  // exactly that way) — the latest start date is the MTD entry.
  const mtd = months.reduce((best, m) => (!best || String(m.start) > String(best.start) ? m : best), null);
  const total = months.reduce((s, m) => s + (m.total || 0), 0);
  return (
    <Box>
      <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="overline" sx={{ color: GREY }}>Month to date</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>{mtd ? fmtMoney(mtd.total, unit) : '—'}</Typography>
        </Box>
        <Box>
          <Typography variant="overline" sx={{ color: GREY }}>
            {tenantId === 'optimum' ? 'Org total' : 'Total'} ({months.length} mo)
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>{fmtMoney(total, unit)}</Typography>
        </Box>
      </Stack>
      <Typography variant="caption" sx={{ color: GREY }}>
        {months.length} calendar month{months.length === 1 ? '' : 's'} including the current month to date —{' '}
        <Link component="button" underline="hover" onClick={() => onNavigate('finops')} sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}>
          open FinOps
        </Link>{' '}for the breakdown.
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// OBSOLESCENCE — the Obsolescence screen's own single-pass analysis, reused,
// summarised to counts per band.
// ---------------------------------------------------------------------------
function ObsolescenceModule({ tenantId, inMaster, refreshToken, onNavigate }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (inMaster) { setState({ loading: false }); return undefined; }
    let live = true;
    setState({ loading: true });
    (async () => {
      try {
        const [its, deps, apps] = await Promise.all([
          store.listFactSheets(tenantId, { type: 'ITComponent' }),
          store.listFactSheets(tenantId, { type: 'Deployment' }),
          store.listFactSheets(tenantId, { type: 'Application' }),
        ]);
        if (!live) return;
        const servers = (its || []).filter((fs) => isServerRecord(specsOf(fs)));
        if (!servers.length) { setState({ loading: false, empty: true }); return; }
        const analysis = analyzeObsolescence(servers, buildLinkage(servers, deps || [], apps || []), new Date());
        setState({ loading: false, counts: analysis.counts, scores: analysis.scores });
      } catch (e) {
        if (live) setState({ loading: false, error: e.message || 'could not read the workspace' });
      }
    })();
    return () => { live = false; };
  }, [tenantId, inMaster, refreshToken]);

  if (inMaster) {
    return (
      <EmptyNote>
        Obsolescence is assessed per client workspace — the Warnings module above carries
        each client's end-of-support counts, and the full report lives under
        Reports → Obsolescence inside a workspace.
      </EmptyNote>
    );
  }
  if (state.loading) return <ModuleLoading />;
  if (state.error) return <Alert severity="error">{state.error}</Alert>;
  if (state.empty) return <EmptyNote>No server records in this workspace yet.</EmptyNote>;

  const bands = [
    { cls: CLASSES.EOS, color: (t) => t.palette.error.main },
    { cls: CLASSES.Y1, color: (t) => t.palette.warning.main },
    { cls: CLASSES.Y2, color: (t) => t.palette.warning.light },
    { cls: CLASSES.OK, color: (t) => t.palette.success.main },
    { cls: CLASSES.UNKNOWN, color: (t) => t.palette.text.disabled },
  ];
  return (
    <Box>
      <Stack spacing={0.75}>
        {bands.map((b) => (
          <Stack key={b.cls} direction="row" alignItems="center" spacing={1}
            onClick={() => onNavigate('obsolescence')} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onNavigate('obsolescence'); }}
            sx={{ px: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: b.color }} />
            <Typography sx={{ fontSize: 12.5, flex: 1 }}>{b.cls}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: HEAD }}>
              {(state.counts[b.cls] || 0).toLocaleString()}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
        {state.scores.total.toLocaleString()} servers assessed; {state.scores.missingDataPct.toFixed(1)}% missing data —
        Unknown is unread, not supported. Same analysis, same precedence (recorded EOS &gt; recorded EOL &gt; OS-name
        inference) as the Obsolescence report.
      </Typography>
    </Box>
  );
}

const MODULE_COMPONENTS = {
  warnings: WarningsModule,
  renewals: RenewalsModule,
  completion: CompletionModule,
  activity: ActivityModule,
  spend: SpendModule,
  obsolescence: ObsolescenceModule,
};

// ---------------------------------------------------------------------------
// The dashboard shell
// ---------------------------------------------------------------------------
export default function MyDashboard({ tenantId, session, onNavigate, onOpenTenant }) {
  const inMaster = tenantId === 'optimum';
  const storageKey = layoutKeyFor(session && session.email, tenantId);
  const [layout, setLayout] = useState(() => readLayout(storageKey));
  const [addAnchor, setAddAnchor] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // Layouts are per user+workspace: switching workspace reloads that
  // workspace's saved arrangement.
  useEffect(() => { setLayout(readLayout(storageKey)); }, [storageKey]);

  const apply = useCallback((next) => {
    setLayout(next);
    writeLayout(storageKey, next);
  }, [storageKey]);

  const move = (key, delta) => {
    const i = layout.indexOf(key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= layout.length) return;
    const next = [...layout];
    [next[i], next[j]] = [next[j], next[i]];
    apply(next);
  };
  const remove = (key) => apply(layout.filter((k) => k !== key));
  const add = (key) => { setAddAnchor(null); if (!layout.includes(key)) apply([...layout, key]); };

  const available = useMemo(
    () => Object.keys(MODULE_DEFS).filter((k) => !layout.includes(k)),
    [layout]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>My dashboard</Typography>
        <HelpBubble topic="myDashboard" />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Re-run every module's checks">
          <IconButton onClick={() => setRefreshToken((n) => n + 1)} aria-label="Refresh dashboard">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button startIcon={<AddIcon />} variant="outlined" disabled={!available.length}
          onClick={(e) => setAddAnchor(e.currentTarget)}>
          Add module
        </Button>
        <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
          {available.map((k) => (
            <MenuItem key={k} onClick={() => add(k)}>{MODULE_DEFS[k].label}</MenuItem>
          ))}
        </Menu>
      </Stack>
      <Typography sx={{ fontSize: 13, color: GREY, mb: 2 }}>
        {inMaster
          ? 'Warnings and figures are grouped per client — nothing on this screen blends two clients into one number.'
          : 'Pick the modules that matter to you; the arrangement is saved per workspace in this browser.'}
      </Typography>

      {!layout.length && (
        <Paper sx={{ p: 4, textAlign: 'center', color: MUTED }}>
          No modules on this dashboard. Add one with &ldquo;Add module&rdquo;.
        </Paper>
      )}

      <Grid container spacing={2}>
        {layout.map((key, idx) => {
          const Def = MODULE_DEFS[key];
          const Component = MODULE_COMPONENTS[key];
          if (!Def || !Component) return null;
          return (
            <Grid item xs={12} md={Def.size} key={key}>
              <ModuleFrame
                title={Def.label}
                canMoveUp={idx > 0}
                canMoveDown={idx < layout.length - 1}
                onMoveUp={() => move(key, -1)}
                onMoveDown={() => move(key, +1)}
                onRemove={() => remove(key)}
              >
                <Component
                  tenantId={tenantId}
                  inMaster={inMaster}
                  refreshToken={refreshToken}
                  onNavigate={onNavigate}
                  onOpenTenant={onOpenTenant}
                />
              </ModuleFrame>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
